import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import { List, arrayMove } from "react-movable";
import Icon from "shared/components/Icon";
import { ICONS } from "shared/components/Icon";
import DotMenu from "shared/components/DotMenu";
import FlowItem from "components/FlowItem";
import FlowItemForm from "components/FlowItemForm";
import Modal from "components/Modal";
import OkWarning from "shared/components/OkWarning";
import bookFlowItemRemove from "redux/actions/books/bookFlowItemRemove";
import ButtonAdd from "shared/components/buttons/ButtonAdd";
import bookChapterAutoSort from "redux/actions/books/bookChapterAutoSort";
import bookFlowItemReorder from "redux/actions/books/bookFlowItemReorder";
import { bookSetDefaultContent } from "redux/actions/books/bookSetDefaultContent";
import { loadConfigAI } from "redux/actions/configAI/configAiLoad";
import { updateConfigAI } from "redux/actions/configAI/configAiUpdate";

import "./style.scss";
import AiRegulations from "components/FlowItemForm/types/AiRegulations"

const ChapterItem = ({
    chapter,
    onEdit,
    onRemove,
    index: chapterIndex,
    osbn,
    zIndexValue,
    chapterId,
    bookId,
    bookStartItem,
    defaultChapters,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [selectedFlowItem, setSelectedFlowItem] = useState(null);
    const [removeFlowItem, setRemoveFlowItem] = useState(null);
    const [isOpen, setOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showAiModal, setShowAiModal] = useState(false);

    const { books } = useSelector(state => state.books);
    const { config } = useSelector(state => state.configAI);
    const book = books[osbn];
    const { chapters } = book;

    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        if (!config || !config.aiMap) return
        const mapItem = config.aiMap.find(item => item.chapterId === chapterId);
        if (!mapItem) return;
        setIsEnabled(mapItem.isEnabled);
    }, [config])

    // Generate chapter order and index map dynamically
    const chapterOrder = Object.keys(chapters);
    const chapterIndexMap = {};
    chapterOrder.forEach((key, index) => {
        chapterIndexMap[key] = index;
    });

    // Helper: insert chapter into defaultChapters in proper order
    const insertDefaultChapter = (defaults, newId) => {
        if (defaults.includes(newId)) return defaults;

        const updated = [...defaults];
        let inserted = false;
        for (let i = 0; i < updated.length; i++) {
            if (chapterIndexMap[newId] < chapterIndexMap[updated[i]]) {
                updated.splice(i, 0, newId);
                inserted = true;
                break;
            }
        }
        if (!inserted) updated.push(newId);
        return updated;
    };

    const { items: flowItems } = chapter;
    const [itemKeys, setItemKeys] = useState(Object.keys(flowItems));

    useEffect(() => {
        dispatch(loadConfigAI(bookId));
    }, [])


    const currentChapter = config?.aiMap?.find(item => item.chapterId === chapterId) ?? null;


    useEffect(() => {
        setItemKeys(Object.keys(flowItems));
    }, [flowItems]);

    const foldClasses = classNames("fold", isOpen ? "open" : "closed");

    const renderConfirmRemove = () => (
        <OkWarning
            title={t("book.flowItem.delete.confirm.title")}
            message={t("book.flowItem.delete.confirm.message")}
            onConfirm={() => {
                dispatch(bookFlowItemRemove(osbn, chapterId, removeFlowItem));
                setRemoveFlowItem(null);
                toggleListItems("visible");
            }}
            onCancel={() => {
                setRemoveFlowItem(false);
                toggleListItems("visible");
            }}
        />
    );

    const validItemKeys = itemKeys.filter((key) => !!flowItems[key]);
    const handleOpenAiModal = () => {
        const navBar = document.querySelector(".main-navigation");
        if (navBar) {
            navBar.style.zIndex = "0";
        }
        setShowAiModal(true);

        toggleListItems("hidden")
    };

    const handleOpenChapterModal = (chapterId) => {
        onEdit(chapterId);
    }

    const handleOpenModal = () => {
        setShowModal(true);
        const navBar = document.querySelector(".main-navigation");
        if (navBar) {
            navBar.style.zIndex = "0";
        }
    }


    const toggleListItems = (visibility) => {
        const elements = document.querySelectorAll('[class*="list-movable-"]');

        elements.forEach((el) => {
            el.style.visibility = visibility;
        });
    };

    const handleCloseAiModal = () => {
        toggleListItems("visible");
        setShowAiModal(false);

        const nav = document.querySelector(".main-navigation");
        if (nav) {
            nav.style.zIndex = "500";
        }
    };

    const handleCloseModal = (zIndexValue) => {
        toggleListItems("visible");
        const nav = document.querySelector(".main-navigation");
        if (nav) {
            nav.style.zIndex = "500";
        }
        setShowModal(false);
        setSelectedFlowItem(null);
    };

    const stripTypename = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(stripTypename);
        } else if (obj && typeof obj === "object") {
            const cleaned = {};
            for (const key in obj) {
                if (key === "__typename") continue;
                cleaned[key] = stripTypename(obj[key]);
            }
            return cleaned;
        }
        return obj;
    };

    const isNotFirstChapter = bookStartItem != chapterId;

    return (
        <div className="chapter-item">
            <div className="chapter-header" draggable="true">
                <div className="header-content">
                    <div onClick={() => setOpen(!isOpen)} className={foldClasses}>
                        <Icon name={isOpen ? ICONS.UP : ICONS.DOWN} />
                    </div>

                    <span className="indicator">{chapterIndex}</span>
                    <span className="label">{chapter.label} {chapterIndex === 1 && " 🥇"}</span>
                </div>

                <div className="header-content">
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="isDefault"
                                color="primary"
                                checked={(defaultChapters ?? []).includes(chapterId)}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    const safeDefaults = defaultChapters ?? [];

                                    const updatedDefaults = isChecked
                                        ? insertDefaultChapter(safeDefaults, chapterId)
                                        : safeDefaults.filter((id) => id !== chapterId);

                                    dispatch(bookSetDefaultContent(osbn, bookStartItem, updatedDefaults));
                                }}
                            />
                        }
                        label={
                            <span style={{ fontWeight: (defaultChapters ?? []).includes(chapterId) ? "600" : "normal" }}>
                                {(defaultChapters ?? []).includes(chapterId)
                                    ? t("book.chapters.default.status.active")
                                    : t("book.chapters.default.status.inactive")}
                            </span>
                        }
                    />
                    {isNotFirstChapter && (bookStartItem !== null) &&
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="isAiEnabled"
                                    color="primary"
                                    checked={currentChapter?.isEnabled ?? false}
                                    onChange={(e) => {
                                        const isChecked = e.target.checked;

                                        const configData = {
                                            id: config.id,
                                            organisationId: config.organisationId,
                                            bookId: config.bookId,
                                            apikey: config.apikey,
                                            prompt: config.prompt,
                                            provider: config.provider,
                                            model: config.model,
                                            osbn,
                                            aiMap: stripTypename(config.aiMap).map(item =>
                                                item.chapterId === chapterId
                                                    ? { ...item, isEnabled: isChecked }
                                                    : item
                                            ),
                                        };
                                        dispatch(updateConfigAI(configData));
                                    }}
                                />
                            }
                            label={
                                <span style={{ fontWeight: (defaultChapters ?? []).includes(chapterId) ? "600" : "normal" }}>
                                    {t("book.chapters.default.ai.status")}
                                </span>
                            }
                        />}

                    <button
                        data-movable-handle
                        style={{
                            cursor: "grab",
                            marginRight: "1em",
                            background: "none",
                            border: "none"
                        }}
                    >
                        <Icon name={ICONS.DRAG} />
                    </button>

                    <div className="actions">
                        <DotMenu
                            onOpen={() => {


                                const dotMenu = document.querySelector(`.${zIndexValue}`);
                                if (dotMenu) dotMenu.style.zIndex = "900";
                            }}
                            onBlur={() => {
                                const dotMenu = document.querySelector(`.${zIndexValue}`);
                                if (dotMenu) dotMenu.style.zIndex = "0";
                            }}
                            menuItems={[
                                { action: () => handleOpenChapterModal(chapter.id), label: t("book.chapter.edit.label") },
                                { action: () => onRemove(chapter.id), label: t("book.chapter.remove.label") },
                                { action: () => dispatch(bookChapterAutoSort(osbn, chapterId)), label: t("book.chapter.autoSort.label") },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {isOpen && (
                <div className="chapter-content">
                    <List
                        values={validItemKeys}
                        onChange={({ oldIndex, newIndex }) => {
                            if (oldIndex === 0 || newIndex === 0) return;

                            const updatedKeys = arrayMove(validItemKeys, oldIndex, newIndex);
                            setItemKeys(updatedKeys);

                            dispatch(bookFlowItemReorder(osbn, chapterId, updatedKeys));
                        }}
                        renderList={({ children, props, isDragged }) => (
                            <ul
                                {...props}
                                style={{
                                    padding: "0 0 1em 0",
                                    cursor: isDragged ? "grabbing" : "inherit"
                                }}
                            >
                                {children}

                                {isEnabled && (
                                    <li
                                        style={{
                                            display: "flex",
                                            background: "#ffffffff",
                                            justifyContent: "space-between",
                                            alignItems: "center",

                                            fontWeight: "600",
                                            borderRadius: "4px",
                                            boxShadow: "0 4px 4px 0 rgba(132, 115, 115, 0.15)"
                                        }}
                                    >
                                        <div style={{ padding: "1rem" }}>
                                            {t("book.chapters.ai.settings")}
                                        </div>
                                        <div className="actions">
                                            <DotMenu
                                                onOpen={() => {
                                                    const dotMenu = document.querySelector(`.${zIndexValue}`);
                                                    if (dotMenu) dotMenu.style.zIndex = "900";
                                                }}
                                                onBlur={() => {
                                                    const dotMenu = document.querySelector(`.${zIndexValue}`);
                                                    if (dotMenu) dotMenu.style.zIndex = "0";

                                                }}
                                                menuItems={[
                                                    { action: () => handleOpenAiModal(zIndexValue), label: t("book.chapter.edit.label") }
                                                ]}
                                            />
                                        </div>
                                    </li>
                                )}
                            </ul>
                        )}
                        renderItem={({ value, props, index }) => (
                            <li
                                className={`list-movable-child-${chapterIndex}-${index}`}
                                {...props}
                                key={value}
                                style={{ ...props.style, listStyleType: "none" }}
                            >
                                {flowItems[value] && (
                                    <FlowItem
                                        item={flowItems[value]}
                                        id={value}
                                        zIndexValue={`list-movable-child-${chapterIndex}-${index}`}
                                        chapterIndex={chapterIndex}
                                        chapterId={chapterId}
                                        index={index}
                                        onEdit={(id) => {
                                            setSelectedFlowItem(id);
                                            handleOpenModal();
                                        }}
                                        onRemove={(id) => setRemoveFlowItem(id)}
                                        chapter={chapter}
                                        osbn={osbn}
                                    />
                                )}
                            </li>
                        )}
                    />
                    <ButtonAdd label={t("book.chapter.addItem")} onClick={() => {
                        handleOpenModal();
                        toggleListItems("hidden");
                    }} />
                </div>
            )}

            {showModal && (
                <Modal
                    title={t("flowItem.modal.title")}
                    onClose={() => handleCloseModal(zIndexValue)}
                    style={{ zIndex: 9999 }}
                    tabs={[
                        {
                            tabName: "info",
                            component: (
                                <FlowItemForm
                                    id={selectedFlowItem}
                                    osbn={osbn}
                                    chapterId={chapterId}
                                    flowItem={selectedFlowItem ? flowItems[selectedFlowItem] : null}
                                    onCancel={() => handleCloseModal(zIndexValue)}
                                />
                            ),
                        },
                    ]}
                />
            )}

            {showAiModal && (
                <Modal
                    title={t("flowItem.ai.modal.title")}
                    onClose={() => handleCloseAiModal(zIndexValue)}
                    style={{ zIndex: 9999 }}
                    tabs={[
                        {
                            tabName: "info",
                            component: (
                                <AiRegulations
                                    onCancel={() => handleCloseAiModal(zIndexValue)}
                                    chapterId={chapterId}
                                    bookId={book.id}
                                />
                            ),
                        },
                    ]}
                />
            )}

            {removeFlowItem && renderConfirmRemove()}
        </div>
    );
};

ChapterItem.propTypes = {
    chapter: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    osbn: PropTypes.string.isRequired,
    chapterId: PropTypes.string.isRequired,
    bookStartItem: PropTypes.string,
    defaultChapters: PropTypes.array,
    zIndexValue: PropTypes.string,
};

export default ChapterItem;
